import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { dashboardApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, BarChart3, PieChart, TrendingUp, AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#14b8a6'];

export default function Statistics() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [basicStats, setBasicStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [detailedResponse, basicResponse] = await Promise.all([
        dashboardApi.getDetailedStats(),
        dashboardApi.getStats()
      ]);
      setStats(detailedResponse.data);
      setBasicStats(basicResponse.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await dashboardApi.exportExcel();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statistiken_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel-Export erfolgreich', {
        description: 'Die Datei wurde heruntergeladen.'
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export fehlgeschlagen', {
        description: 'Die Excel-Datei konnte nicht erstellt werden.'
      });
    } finally {
      setExporting(false);
    }
  };

  // Monatsnamen für deutsche Anzeige
  const formatMonth = (monthStr) => {
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const hasData = basicStats?.total > 0;

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="statistics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Statistiken</h1>
            <p className="text-muted-foreground">Übersicht über alle Inspektionen und Mängel</p>
          </div>
          {hasData && (
            <Button 
              onClick={handleExportExcel} 
              disabled={exporting}
              className="gap-2"
              data-testid="export-excel-button"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              Excel Export
            </Button>
          )}
        </div>

        {!hasData ? (
          <Card className="p-12 text-center">
            <BarChart3 className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Noch keine Daten vorhanden</h3>
            <p className="text-muted-foreground">
              Erstellen Sie Ihre erste Inspektion, um Statistiken zu sehen.
            </p>
          </Card>
        ) : (
          <>
            {/* Übersichtskarten */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <BarChart3 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gesamt</p>
                      <p className="text-2xl font-bold">{basicStats?.total || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Abgeschlossen</p>
                      <p className="text-2xl font-bold">{basicStats?.completed || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <PieChart className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">In Bearbeitung</p>
                      <p className="text-2xl font-bold">{basicStats?.in_progress || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Entwürfe</p>
                      <p className="text-2xl font-bold">{basicStats?.draft || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Monatliche Inspektionen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Inspektionen pro Monat
                  </CardTitle>
                  <CardDescription>Entwicklung der letzten 12 Monate</CardDescription>
                </CardHeader>
                <CardContent>
                  {stats?.monthly?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={stats.monthly}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis 
                          dataKey="month" 
                          tickFormatter={formatMonth}
                          stroke="#888"
                          fontSize={12}
                        />
                        <YAxis stroke="#888" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a1a', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }}
                          labelFormatter={formatMonth}
                        />
                        <Bar dataKey="total" name="Gesamt" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="completed" name="Abgeschlossen" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      Keine monatlichen Daten vorhanden
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Gebäudetypen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Nach Gebäudetyp
                  </CardTitle>
                  <CardDescription>Verteilung der Inspektionen</CardDescription>
                </CardHeader>
                <CardContent>
                  {stats?.building_types?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <RePieChart>
                        <Pie
                          data={stats.building_types}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ name, percent }) => `${name.slice(0, 15)}${name.length > 15 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {stats.building_types.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1a1a1a', 
                            border: '1px solid #333',
                            borderRadius: '8px'
                          }}
                        />
                      </RePieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      Keine Gebäudetyp-Daten vorhanden
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Häufigste Mängel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Häufigste Mängel
                </CardTitle>
                <CardDescription>Top 10 der am häufigsten festgestellten Mängel</CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.top_defects?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={stats.top_defects} 
                      layout="vertical"
                      margin={{ left: 20, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#888" fontSize={12} />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={200}
                        stroke="#888"
                        fontSize={11}
                        tickFormatter={(value) => value.length > 30 ? value.slice(0, 30) + '...' : value}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="count" name="Anzahl" fill="#ef4444" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <AlertTriangle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                      <p>Keine Mängel festgestellt</p>
                      <p className="text-sm">Alle Prüfpunkte wurden bestanden!</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
